import { zodResolver } from "@hookform/resolvers/zod";
import { useBankServiceCapabilitiesByIfsc } from "creso-core/hooks";
import { UnboxPromise } from "creso-core/types";
import {
  getBankAccountTypeOptions,
  getBankServiceCapabilityWarningMessage,
} from "creso-core/utils";
import { BankDetailsSchema } from "creso-core/zod";
import {
  CheckCheckIcon,
  InfoIcon,
  Loader2Icon,
  TriangleAlertIcon,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormField,
  FormInput,
  FormSelect,
  VisuallyHidden,
} from "web-design-system";
import { z } from "zod";
import { AccountCenterFormValues } from "./form-schema";
import { useAccountForm } from "./context";
import { useBankVerification } from "./use-bank-verification";
import { ConditionalRenderer } from "../primitives";

export function AddEditBankDetailsDialog({
  trigger,
  data,
  investmentAccountId,
  onUpdate,
}: {
  investmentAccountId: string;
  trigger: React.ReactNode;
  data?: NonNullable<AccountCenterFormValues["bank"][number]>;
  onUpdate: (data: {
    accountNumber: string;
    type: string;
    ifsc: string;
  }) => Promise<boolean> | boolean;
}) {
  const formData = data;
  const { client, onReviewChanges } = useAccountForm();
  const accountTypeOptions = getBankAccountTypeOptions(client);

  const [open, setOpen] = useState(false);

  const form = useForm({
    values: {
      accountNumber: formData?.account_number || "",
      type: formData?.type || accountTypeOptions[0].value,
      ifsc: formData?.ifsc_code || "",
      confirmAccountNumber: formData?.account_number || "",
      primaryAccountHolderName: formData?.primary_account_holder_name || "",
    },
    resolver: zodResolver(BankDetailsSchema),
  });

  const { verify, isVerifying } = useBankVerification();

  const ifscCode = form.watch("ifsc");
  const { data: bankServiceCapabilities } = useBankServiceCapabilitiesByIfsc(
    ifscCode,
    {
      enabled: ifscCode.length >= 11,
    }
  );

  const [verificationResult, setVerificationResult] = useState<UnboxPromise<
    ReturnType<typeof verify>
  > | null>(null);

  const onSubmit = async (values: z.infer<typeof BankDetailsSchema>) => {
    setVerificationResult(null);

    const result = await verify(
      investmentAccountId,
      values.accountNumber,
      values.ifsc,
      values.primaryAccountHolderName as string
    );

    setVerificationResult(result);

    form.reset(
      {
        accountNumber: values.accountNumber,
        confirmAccountNumber: values.accountNumber,
        ifsc: values.ifsc,
        type: values.type,
        primaryAccountHolderName: values.primaryAccountHolderName,
      },
      {
        keepIsSubmitted: true,
        keepIsSubmitSuccessful: true,
      }
    );
  };

  const { isDirty, isSubmitted } = form.formState;

  const bankCapabilityWarningMessage = getBankServiceCapabilityWarningMessage(
    bankServiceCapabilities
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="h-150 w-100"
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>
              {formData?.bank_account_id ? "Edit" : "Add"} bank account
            </DialogTitle>
            <DialogDescription>
              Dialog to update bank account details
            </DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <Form {...form}>
          <form
            className="flex flex-col flex-1 gap-4"
            onSubmit={(event) => {
              event.stopPropagation();
              return form.handleSubmit(onSubmit)(event);
            }}
          >
            <h4 className="mb-2">
              {formData?.bank_account_id ? "Edit" : "Add"} bank account
            </h4>
            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormInput
                  disabled={isVerifying}
                  label="Account number"
                  placeholder="1234567890"
                  {...field}
                />
              )}
            />
            <FormField
              control={form.control}
              name="confirmAccountNumber"
              render={({ field }) => (
                <FormInput
                  disabled={
                    !form.getFieldState("accountNumber").isDirty || isVerifying
                  }
                  label="Confirm account number"
                  {...field}
                />
              )}
            />
            <FormField
              control={form.control}
              name="ifsc"
              render={({ field }) => (
                <FormInput
                  disabled={isVerifying}
                  placeholder="SBIN0000489"
                  label="IFSC code"
                  {...field}
                  onChange={(value) => {
                    field.onChange(value?.toLocaleUpperCase() ?? "");
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormSelect
                  disabled={isVerifying}
                  placeholder="Select account type"
                  options={accountTypeOptions}
                  label="Account type"
                  {...field}
                />
              )}
            />
            <div className="mt-auto flex w-full text-foreground">
              <ConditionalRenderer condition={isVerifying}>
                <div className="flex flex-col w-full text-sm leading-5 gap-2 bg-tag-slate text-tag-slate-foreground rounded-lg p-3">
                  <div className="flex gap-2 items-center">
                    <Loader2Icon className="size-4 animate-spin flex-shrink-0" />
                    <span className="font-semibold tracking-tight">
                      Account will be verified by sending ₹1
                    </span>
                  </div>
                  <span>
                    Verifying account details. This may take upto 15 seconds.
                  </span>
                </div>
              </ConditionalRenderer>

              <ConditionalRenderer condition={!isVerifying}>
                <ConditionalRenderer condition={!isDirty && isSubmitted}>
                  <ConditionalRenderer
                    condition={verificationResult?.status === "failed"}
                  >
                    <div className="flex gap-2 p-3 w-full text-sm leading-5 rounded-lg bg-tag-orange text-tag-orange-foreground items-start">
                      <TriangleAlertIcon className="size-4 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold">
                          Verification Failed
                        </span>
                        <span className="text-sm">
                          {verificationResult?.message}
                        </span>
                      </div>
                    </div>
                  </ConditionalRenderer>
                  <ConditionalRenderer
                    condition={verificationResult?.status === "verified"}
                  >
                    <div className="flex flex-col gap-1 px-1.5 py-3 w-full text-sm items-center leading-5 rounded-lg bg-tag-green text-tag-green-foreground">
                      <div className="flex gap-2 items-center justify-start w-full px-1.5">
                        <CheckCheckIcon className="size-4 text-tag-green-foreground flex-shrink-0" />
                        <span className="font-semibold">Verified</span>
                      </div>
                      {bankCapabilityWarningMessage ? (
                        <div className="flex flex-row gap-2 items-center w-full px-2">
                          <InfoIcon className="size-3.5 flex-shrink-0 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {bankCapabilityWarningMessage}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </ConditionalRenderer>
                </ConditionalRenderer>
              </ConditionalRenderer>
            </div>
            <DialogFooter className="gap-5">
              {/** If verification is not triggered, show the Verify button */}
              <ConditionalRenderer
                condition={isDirty || verificationResult?.status !== "verified"}
              >
                <Button
                  className="w-full flex-1"
                  type="submit"
                  disabled={isVerifying || !isDirty}
                >
                  {isVerifying && <Loader2Icon className="animate-spin" />}
                  {isVerifying ? "Verifying..." : "Verify"}
                </Button>
              </ConditionalRenderer>
              <ConditionalRenderer
                condition={
                  !isDirty &&
                  isSubmitted &&
                  verificationResult?.status === "verified"
                }
              >
                <Button
                  className="flex-1 w-full"
                  type="button"
                  onClick={async () => {
                    const result = await onUpdate({
                      accountNumber: form.watch("accountNumber"),
                      ifsc: form.watch("ifsc"),
                      type: form.watch("type"),
                    });
                    if (result) {
                      setOpen(false);
                      onReviewChanges?.();
                    }
                  }}
                >
                  Proceed
                </Button>
              </ConditionalRenderer>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
